/**
 * The auth module's service: every authentication call the admin makes.
 *
 * WHY A MODULE-OWNED SERVICE
 * --------------------------
 * The repository's module pattern gives each module a `services/` directory that is
 * the ONLY place its API calls live. The auth module had pages but no service, so
 * there was nowhere for a real sign-in to live and the login page faked one. This
 * file is that place.
 *
 * THE CREDENTIAL
 * --------------
 * The session is a first-party `HttpOnly` cookie set by the provider. The admin
 * never sees, stores or forwards the token: the browser sends it back on its own,
 * and `credentials: 'same-origin'` in the transport carries it. That is why the
 * sign-in response schema parses the token and then nothing extracts it.
 *
 * THE ORIGIN
 * ----------
 * The provider refuses a state-changing request that does not state its origin, and
 * it compares that origin against its trusted set (derived from `AUTH_BASE_URL`).
 * A same-origin deployment — the SPA and the API behind one edge — produces the
 * right origin on its own. This service therefore does NOT set an `Origin` header:
 * the browser sets it, and a header this code invented would be a second, weaker
 * statement of the same fact.
 *
 * THE PROVIDER'S ERROR SHAPE
 * --------------------------
 * The provider's own routes answer a refusal with `{ message, code }`, NOT with the
 * shared API error envelope — they are the provider's routes, not platform
 * operations. The transport recognises that shape and carries the provider's CODE
 * through onto the `ApiError`; this service maps that CODE to a stable reason.
 * The mapping lives here, in the module that owns these routes, rather than
 * loosening the shared envelope for every caller.
 */
import { http } from '@/api/client/http';
import { ApiError } from '@/api/client/problem';
import {
  SessionProjectionSchema,
  SignInResponseSchema,
  SignOutResponseSchema,
  type SessionProjection,
} from '@/api/schemas/auth';

export type SignInCredentials = {
  readonly email: string;
  readonly password: string;
};

/**
 * Why a sign-in did not produce a session.
 *
 * These are STABLE names the login form renders. They are not the provider's raw
 * codes, because a provider code is not something the UI should branch on.
 */
export type SignInRefusal =
  /** The credential was refused. Deliberately does not distinguish "no such user" from "wrong password". */
  | 'invalid_credentials'
  /** The identity exists but has not been verified. */
  | 'email_not_verified'
  /** The server does not trust the address this console is served from. */
  | 'untrusted_origin'
  /** Too many attempts. */
  | 'rate_limited'
  /** The server could not answer. */
  | 'unavailable';

export type SignInOutcome = { readonly ok: true } | { readonly ok: false; readonly refusal: SignInRefusal };

export const authApi = {
  /**
   * Sign in through the provider.
   *
   * Never throws: a refused sign-in is an OUTCOME the form renders, not an
   * exception it has to catch. `skipAuthRefresh` is set because a 401 here is the
   * endpoint's own answer — a refused credential, not a session that needs
   * refreshing — and treating it as one would make a failed login recurse.
   */
  async signIn(credentials: SignInCredentials): Promise<SignInOutcome> {
    try {
      await http.post('/auth/sign-in/email', SignInResponseSchema, {
        body: { email: credentials.email, password: credentials.password },
        skipAuthRefresh: true,
      });
      return { ok: true };
    } catch (error) {
      return { ok: false, refusal: refusalFor(error) };
    }
  },

  /**
   * Sign out through the provider.
   *
   * The provider clears the cookie. The platform's own revocation route
   * (`POST /api/v1/identity/sessions/revoke`) is a SECOND step — it writes the
   * audit row and marks the session dead in the liveness store — and it is
   * attempted separately, because a provider sign-out that succeeds must not leave
   * the platform believing the session is still live.
   */
  async signOut(): Promise<void> {
    try {
      await http.post('/auth/sign-out', SignOutResponseSchema, {
        skipAuthRefresh: true,
      });
    } catch {
      // A sign-out that the server could not complete still ends the LOCAL session:
      // the cookie is the browser's to drop, and leaving the user signed in because
      // the network failed is the worse of the two outcomes.
    }
  },

  /**
   * Read the current session.
   *
   * A 401 is the NORMAL answer for a visitor who is not signed in, so it is turned
   * into `null` rather than an exception: "nobody is signed in" is a state, not a
   * failure. Every other failure propagates, because a transport fault must not be
   * mistaken for "not signed in" — that would show the login screen to a signed-in
   * user and hide the real problem.
   */
  async loadSession(): Promise<SessionProjection | null> {
    try {
      return await http.get('/identity/session', SessionProjectionSchema);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) return null;
      throw error;
    }
  },
};

/**
 * Map a transport failure to a stable refusal.
 *
 * THE CODE IS LOAD-BEARING, NOT THE STATUS
 * ----------------------------------------
 * The provider answers with its own codes, and two of its refusals SHARE a status:
 * `INVALID_ORIGIN` and `EMAIL_NOT_VERIFIED` are both 403. A mapping that branched
 * on the status alone would tell a user whose console is served from an untrusted
 * address that their identity has not been verified — a different problem, with a
 * different fix, and a false statement about the account they just typed.
 *
 * So the CODE decides, and the status is only a fallback for a refusal whose code
 * this client does not know. An unrecognised 403 is reported as `unavailable`
 * rather than as `email_not_verified`: the server did not say the identity is
 * unverified, so this client does not say it either.
 *
 * It is EXPORTED because the sign-in page has more than one step that can fail, and
 * every one of them must be reported in the SAME vocabulary. A page that reported a
 * throttle as "the service is unavailable" would send an operator to the wrong desk:
 * one problem waits, the other does not.
 */
export function refusalFor(error: unknown): SignInRefusal {
  if (!(error instanceof ApiError)) return 'unavailable';

  switch (error.code) {
    case 'INVALID_EMAIL_OR_PASSWORD':
    case 'USER_NOT_FOUND':
      return 'invalid_credentials';
    case 'EMAIL_NOT_VERIFIED':
      return 'email_not_verified';
    case 'INVALID_ORIGIN':
      return 'untrusted_origin';
    case 'RATE_LIMITED':
      return 'rate_limited';
    default:
      break;
  }

  if (error.status === 401) return 'invalid_credentials';
  if (error.status === 429) return 'rate_limited';
  return 'unavailable';
}
